import { Component, Element, Event, EventEmitter, Host, Method, Prop, State, h } from '@stencil/core';
import { AutoComplete, ButtonIcon, Icon, InputMode, Type } from './types';

import {
  FormComponent,
  HiddenFormElementSlot,
  Validator,
  closestElementCrossShadowBoundary,
  connectForm,
  defaultValidator,
  disconnectForm,
  getLabelText,
  guid,
  hasText,
  initializeValidators,
  mapGet,
} from '../../utils';

/** @slot - Adds label text. */
@Component({
  shadow: true,
  styleUrl: 'input.css',
  tag: 'abds-input',
})
export class Input implements FormComponent {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsInputElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /**
   * Hint for form autofill feature.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#autocomplete
   */
  @Prop({ reflect: true }) autocomplete: AutoComplete;

  /** Configurable label/title text for interactive button inside of input (search's clear button and password's mask button). */
  @Prop({ reflect: true }) interactiveButtonLabel: string;

  /** Disables input from interaction. */
  @Prop({ reflect: true }) disabled = false;

  /** Allows setting error state. */
  @Prop({ reflect: true }) error = false;

  /** Custom error message. Will only display if `error` prop is true. */
  @Prop({ reflect: true }) errorMessage: string;

  /** Text of the information message. */
  @Prop({ reflect: true }) helperText: string;

  /** Hide icon-end, if present. */
  @Prop({ reflect: true }) hideIconEnd = false;

  /** Hide icon-start, if present. */
  @Prop({ reflect: true }) hideIconStart = false;

  /** Adds an abds-icon to end of input. */
  @Prop() iconEnd: string;

  /** Adds an abds-icon to beginning of input. */
  @Prop() iconStart: string;

  /** Sets inputmode attribute for input */
  @Prop({ reflect: true }) inputmode: InputMode;

  /** The maximum value, which must not be less than its minimum (min attribute) value. */
  @Prop({ reflect: true }) max: string;

  /** This attribute specifies the maximum number of characters that the user can enter. */
  @Prop({ reflect: true }) maxlength: string;

  /** The minimum value, which must not be greater than its maximum (max attribute) value. */
  @Prop({ reflect: true }) min: string;

  /** This attribute specifies the minimum number of characters that the user can enter. */
  @Prop({ reflect: true }) minlength: string;

  /** Specifies a custom error message for invalid minLength. */
  @Prop() minlengthMessage: string;

  /** The name of the control, which is submitted with the form data. */
  @Prop({ reflect: true }) name!: string;

  /**
   * A regular expression that the value is checked against.
   * The pattern must match the entire value, not just some subset.
   */
  @Prop({ reflect: true }) pattern: string;

  /** Specifies a custom error message for invalid pattern. */
  @Prop() patternMessage: string;

  /** Instructional text that shows before the input has a value. */
  @Prop({ reflect: true }) placeholder: string;

  /** When set to true, it makes the input not mutable. */
  @Prop({ reflect: true }) readonly = false;

  /** Specifies required state for form submission. */
  @Prop({ reflect: true }) required = false;

  /** Specifies a custom error message for required state. */
  @Prop() requiredMessage: string;

  /** The tooltip text to be passed to label. */
  @Prop({ reflect: true }) tooltip: string;

  /** Specify the input type. */
  @Prop({ reflect: true }) type: Type = 'text';

  /** The value of the input. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) value: string;

  /** Sets input width. */
  @Prop({ reflect: true }) width: 'default' | 'full' = 'default';

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /**
   * Map of Input Type Default Icons
   * in the case that no value for prop iconStart is provided by implementer, we want to show these icons by default
   * */
  private defaultIcons: Record<string, string> = {
    search: 'search',
  };

  /** Hidden input element. */
  hiddenElement: HTMLInputElement;

  /** Unique id to input. */
  private inputId: string;

  /** Initial render status. */
  private isInitialRender = true;

  /** Text to be used for label. */
  private labelText: string;

  /** Native element. */
  private nativeInput: HTMLInputElement;

  /** Instance validation. */
  private validator: Validator<any> = defaultValidator();

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private onBlur = (): void => {
    this.hasFocus = false;
    this.setIsInvalid();
  };

  private onChange = (): void => {
    this.abdsChange.emit();
  };

  private onFocus = (): void => {
    this.setFocus();
  };

  private onInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;

    this.value = input.value;
    this.abdsInput.emit();
    this.setIsInvalid();
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      const form = closestElementCrossShadowBoundary(this.host, 'form') as HTMLFormElement;

      if (form.checkValidity()) form.submit();
    }

    if (this.type !== 'number' || this.disabled || this.readonly) return;
    // TODO: add "event: KeyboardEvent" as a parameter and insert logic for arrow up/down on number type here.
  };

  private renderIcon = (iconPosition: 'start' | 'end'): Node | null => {
    const isIconStart = iconPosition === 'start';
    const shouldHideIcon = isIconStart ? this.hideIconStart : this.hideIconEnd;
    const icon = isIconStart ? this.iconStart : this.iconEnd;

    const clear = () => {
      this.value = null;
      this.nativeInput.value = '';
      this.setIsInvalid();
    };

    // stopPropagation is needed to prevent keyboard events from propagating events to input.
    const stopPropagation = (event: KeyboardEvent) => {
      event.stopPropagation();
    };

    const togglePasswordMask = (event: MouseEvent): void => {
      // stopPropagation is needed to keep focus on the button.
      event.stopPropagation();
      this.nativeInput.type = this.nativeInput.type === 'password' ? 'text' : 'password';

      this.setPasswordIcon(this.nativeInput.type === 'password' ? 'view-1' : 'view-off');
    };

    const renderIcon = (icon: Icon, shouldRender = true): Node =>
      shouldRender ? <abds-icon {...icon} size="md" stroke-width="semibold"></abds-icon> : null;

    // TODO: Replace with abdsButtonIcon when component is ready and delete the  `ButtonIcon` from types file
    const renderButtonIcon = ({ icon, ...button }: Partial<ButtonIcon>, shouldRender: boolean): Node | null =>
      shouldRender ? <button {...button}>{renderIcon(icon)}</button> : null;

    const renderIconFunctionMap: Record<string, () => Node | null> = {
      none: (): null => null,
      password: (): Node =>
        isIconStart
          ? renderIcon({ class: 'icon-start', icon: this.iconStart }, this.iconStart && !shouldHideIcon)
          : renderButtonIcon(
              {
                'aria-label': this.interactiveButtonLabel || 'Show/hide value',
                icon: {
                  icon: this.passwordIcon,
                },
                onClick: togglePasswordMask,
                onKeyDown: stopPropagation,
                title: this.interactiveButtonLabel || 'Show/hide value',
              },
              !(this.disabled || this.readonly)
            ),
      search: (): Node =>
        isIconStart
          ? renderIcon(
              { class: 'icon-start', icon: this.iconStart || this.defaultIcons[this.type] },
              !!(this.iconStart || this.defaultIcons[this.type]) && !shouldHideIcon
            )
          : renderButtonIcon(
              {
                'aria-label': this.interactiveButtonLabel || 'Clear search entry',
                icon: { icon: 'remove' },
                onClick: clear,
                onKeyDown: stopPropagation,
                title: this.interactiveButtonLabel || 'Clear search entry',
              },
              this.value?.length > 0 && !(this.disabled || this.readonly)
            ),
      text: (): Node => renderIcon({ class: `icon-${iconPosition}`, icon }, !!icon && !shouldHideIcon),
    };

    const renderIconFunction = mapGet(this.type, renderIconFunctionMap, 'none') as () => Node | null;

    return renderIconFunction();
  };

  private setIsInvalid = (isInvalid = !this.validator.validate(this.nativeInput)): void => {
    this.isInvalid = isInvalid;
  };

  private setPasswordIcon = (icon: string): void => {
    this.passwordIcon = icon;
  };

  //--------------------------------------------------------------------------
  //
  //  Private State
  //
  //--------------------------------------------------------------------------

  /** Determine if input has focus. */
  @State() private hasFocus = false;

  /** Determine if input component value is in invalid state. */
  @State() private isInvalid = false;

  /** Determine password icon name. */
  @State() private passwordIcon = 'view-1';

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  /**
   * Sets focus on native `input` in `abds-checkbox`.
   * @internal
   */
  @Method()
  async setFocus(): Promise<void> {
    this.nativeInput?.focus();

    this.hasFocus = true;
  }

  //--------------------------------------------------------------------------
  //
  //  Events
  //
  //--------------------------------------------------------------------------

  /** Fired only when value is committed (e.g. pressing the enter key). */
  @Event() abdsChange: EventEmitter;

  /** Fired every time value changes. */
  @Event() abdsInput: EventEmitter;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentDidRender() {
    if (this.isInitialRender) this.isInitialRender = false;
  }

  componentWillLoad() {
    this.validator = initializeValidators(this);
    this.inputId = this.host.id || `input-${guid()}`;
  }

  connectedCallback() {
    this.labelText = getLabelText(this.host);
    connectForm(this);
  }

  disconnectedCallback() {
    disconnectForm(this);
  }

  render() {
    const hasSupportingText = (this.error && this.errorMessage) || this.helperText || this.isInvalid;

    return (
      <Host onClick={this.disabled ? null : this.onFocus} onKeyDown={this.disabled ? null : this.onKeyDown}>
        {hasText(this.host) && (
          <abds-label required={this.required} tooltip={this.tooltip}>
            <slot />
          </abds-label>
        )}
        <div
          class={{
            'input-container': true,
            invalid: !this.isInitialRender && this.isInvalid,
            'has-focus': this.hasFocus,
          }}
        >
          {this.renderIcon('start')}
          <input
            aria-describedby={hasSupportingText ? this.inputId : undefined}
            aria-label={this.labelText || undefined}
            autocomplete={this.autocomplete}
            disabled={this.disabled}
            input-mode={this.inputmode}
            max={this.max}
            maxlength={this.maxlength}
            min={this.min}
            minlength={this.minlength}
            name={this.name}
            onBlur={this.onBlur}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onInput={this.onInput}
            pattern={this.pattern}
            placeholder={this.placeholder}
            readonly={this.readonly}
            ref={(input) => (this.nativeInput = input)}
            required={this.required}
            type={this.type}
            value={this.value || ''}
          />
          {this.renderIcon('end')}
        </div>
        {hasSupportingText && (
          <abds-supporting-text
            error-text={
              (this.isInvalid && this.validator.errorMessage) || (this.error && this.errorMessage) || undefined
            }
            helper-text={this.helperText}
            id={this.inputId}
          ></abds-supporting-text>
        )}
        <HiddenFormElementSlot component={this} type={this.type} />
      </Host>
    );
  }
}
